import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema,
} from "@capacitor/push-notifications";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LockIcon from "@mui/icons-material/Lock";
import MailIcon from "@mui/icons-material/Mail";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import RouteIcon from "@mui/icons-material/Route";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { AddNewEventButton } from "common/components";
import { InitialScreenSelectLogo } from "common/icons";
import AgriwebbLogoIcon from "common/icons/AgriwebbLogoIcon";
import { useTranslation } from "common/locales";
import {
  ExternalProvider,
  externalProviderName,
} from "features/signup/Oauth2RedirectScreen";
import {
  changePassword,
  fetchUserById,
  getAllFieldGeoms,
  initializeApp,
  initializeFarm,
  openPushNotificationModal,
  removeOauth2Client,
  selectCurrentFarm,
  setZoomToGeom,
  useAppDispatch,
  useAppSelector,
} from "model";
import { selectAllAccessibleFarmerDashboards } from "model/accessibleFarmerDashboardsSlice";
import { selectAllFarmerProgrammeDashboards } from "model/farmerDashboardsSlice";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

const InitialSelectScreen = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { formatMessage: f } = useIntl();
  const { notify } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentFarm = useAppSelector(selectCurrentFarm);
  const currentFarmId = currentFarm?.guid;
  const fieldGeoms = useAppSelector(getAllFieldGeoms);
  const user = useAppSelector((state) => state.user.details);

  const farmerDashboards = useAppSelector(selectAllFarmerProgrammeDashboards);
  const accessibleFarmerDashboards = useAppSelector(
    selectAllAccessibleFarmerDashboards
  );

  // Profile dialog state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [password, setPassword] = useState("");

  const setupPushNotifications = () => {
    // Only setup push notifications on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    // Push notification setup is now handled in the device registration utility
    // This function is kept for backward compatibility but the actual setup
    // happens during app initialization and login/signup flows

    // Set up push notification received listener
    void PushNotifications.addListener(
      "pushNotificationReceived",
      (notification: PushNotificationSchema) => {
        console.log("Push received: " + JSON.stringify(notification));
        handlePushNotification(notification);
      }
    );

    void PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification: ActionPerformed) => {
        console.log("Push action performed: " + JSON.stringify(notification));
        // Handle when user opens the app from the push notification
        if (notification.notification?.data) {
          handlePushNotification(notification.notification);
        }
      }
    );
  };

  const handlePushNotification = (notification: PushNotificationSchema) => {
    if (notification.data?.type === "file_entry") {
      // Handle file sharing notification
      const fileName = notification.data.fileName;
      const uploaderName = notification.data.uploaderName;

      if (fileName && uploaderName) {
        dispatch(openPushNotificationModal({ fileName, uploaderName }));
      }
    }
  };

  // Profile dialog handlers
  const handleProfileDialogOpen = () => {
    setProfileDialogOpen(true);
  };

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
    setEditingPassword(false);
  };

  const handlePasswordSubmit = async () => {
    if (password) {
      await dispatch(changePassword(password)).then(unwrapResult);
      notify(t("passwordChange.success"));
      setEditingPassword(false);
      mixpanel.track("Password changed");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    dispatch({ type: "RESET_STORE" }); // see rootReducer
    mixpanel.track("Logout successful");
    mixpanel.reset();
    history.replace("/");
  };

  const removeIntegration = async (externalProvider: ExternalProvider) => {
    await dispatch(removeOauth2Client(externalProvider)).then(unwrapResult);
    await dispatch(fetchUserById(user!.guid)).then(unwrapResult);
    notify(
      t("oauth2ClientRemove.success", {
        providerName: externalProviderName[externalProvider],
      })
    );
  };

  useEffect(() => {
    // Set up deep link listener
    const handleAppUrlOpen = ({ url }: { url: string }) => {
      console.log("Deep link opened: ", url);
    };

    // Add the listener when component mounts
    void App.addListener("appUrlOpen", handleAppUrlOpen);
  }, []);

  useEffect(() => {
    if (!currentFarm) {
      const initApp = async () => {
        const res = await dispatch(initializeApp()).then(unwrapResult);
        const farms = res?.farms;
        if (farms && farms.length > 0) {
          const defaultFarm = farms[0];
          await dispatch(initializeFarm(defaultFarm));
          setupPushNotifications();
        }
      };
      void initApp();
    }
    if (fieldGeoms.features.length > 0) {
      dispatch(setZoomToGeom(fieldGeoms));
    } else {
      if (currentFarm?.geom) {
        dispatch(setZoomToGeom(currentFarm.geom));
      }
    }
    // we need to disable lint here because we cannot have dependency on an object - it would keep zooming otherwise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFarm?.guid, dispatch]);

  const isCorporateUser = !!(
    user?.corporations?.length && user.corporations.length > 0
  );

  // Redirect to farm view on mobile since we have bottom tab navigation
  useEffect(() => {
    if (isMobile && currentFarmId) {
      history.replace(`/farm/${currentFarmId}`);
    }
  }, [isMobile, currentFarmId, history]);

  // For web (desktop), show new Figma design
  const currentRoute = history.location.pathname;
  let profileText = "Profile";
  if (user?.name && !user?.name.includes("@")) {
    profileText = user.name;
  }

  return (
    <DrawerContainer>
      {/* Logo */}
      <LogoWrapper>
        <InitialScreenSelectLogo color="#1C4321" />
      </LogoWrapper>

      {/* Content Wrapper */}
      <ContentWrapper>
        {/* Navigation Buttons */}
        <NavButtonsContainer>
          {!isCorporateUser && (
            <NavButton
              active={currentRoute.includes("/farm/")}
              onClick={() => {
                mixpanel.track("Go to landmanagement and grazing clicked");
                history.push(`/farm/${currentFarmId || ""}`);
              }}
            >
              <FlagOutlinedIcon
                sx={{ width: 20, height: 20, color: "inherit" }}
              />
              <span>{t("go.to.landmanagement.and.grazing.button")}</span>
            </NavButton>
          )}

          {!isCorporateUser && farmerDashboards.length > 0 && (
            <NavButton
              active={currentRoute === "/farm-insights"}
              onClick={() => {
                mixpanel.track("Go to farm insights clicked");
                history.push(`/farm-insights`);
              }}
            >
              <InsightsOutlinedIcon
                sx={{ width: 20, height: 20, color: "inherit" }}
              />
              <span>{t("go.to.farm.insights")}</span>
            </NavButton>
          )}

          {isCorporateUser && (
            <NavButton
              active={currentRoute === "/accessible-farms-insights"}
              onClick={() => {
                mixpanel.track("Go to accessible farms insights clicked");
                history.push(`/accessible-farms-insights`);
              }}
            >
              <InsightsOutlinedIcon
                sx={{ width: 20, height: 20, color: "inherit" }}
              />
              <span>{t("go.to.accessible.farms.insights")}</span>
            </NavButton>
          )}

          {!isCorporateUser && (
            <NavButton
              active={currentRoute === "/programmes"}
              onClick={() => {
                mixpanel.track("Go to programmes clicked");
                history.push(`/programmes`);
              }}
            >
              <RouteIcon sx={{ width: 20, height: 20, color: "inherit" }} />
              <span>{t("go.to.programmes")}</span>
            </NavButton>
          )}

          {isCorporateUser && (
            <NavButton
              active={currentRoute === "/project-insights"}
              onClick={() => {
                mixpanel.track("Go to project insights clicked");
                history.push(`/project-insights`);
              }}
            >
              <RouteIcon sx={{ width: 20, height: 20, color: "inherit" }} />
              <span>{t("go.to.project.insights")}</span>
            </NavButton>
          )}

          {!isCorporateUser && (
            <NavButton
              active={currentRoute === "/inputs-and-outputs"}
              onClick={() => {
                mixpanel.track("Go to inputs and outputs clicked");
                history.push(`/inputs-and-outputs`);
              }}
            >
              <SwapHorizIcon sx={{ width: 20, height: 20, color: "inherit" }} />
              <span>{t("go.to.inputs.and.outputs")}</span>
            </NavButton>
          )}

          <NavButton
            active={currentRoute === "/file-management-overview"}
            onClick={() => {
              mixpanel.track("Go to file management view clicked");
              history.push(`/file-management-overview`);
            }}
          >
            <FolderOutlinedIcon
              sx={{ width: 20, height: 20, color: "inherit" }}
            />
            <span>{t("go.to.file.management.button")}</span>
          </NavButton>
        </NavButtonsContainer>

        {/* Add New Event Button */}
        {!isCorporateUser && <AddNewEventButton maxWidth="150px" />}
      </ContentWrapper>

      {/* Profile at bottom */}
      <ProfileWrapper>
        <ProfileButton>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <AccountBoxIcon sx={{ width: 20, height: 20, flexShrink: 0 }} />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "wrap",
              }}
            >
              {profileText}
            </span>
          </Box>
          <IconButton
            onClick={handleProfileDialogOpen}
            size="small"
            sx={{ padding: 0, flexShrink: 0 }}
          >
            <MoreVertIcon sx={{ width: 20, height: 20, color: "#71717a" }} />
          </IconButton>
        </ProfileButton>
      </ProfileWrapper>

      {/* Profile Dialog */}
      {user && (
        <Dialog
          open={profileDialogOpen}
          onClose={handleProfileDialogClose}
          aria-labelledby="profile-dialog-title"
        >
          <ProfileDialogTitle id="profile-dialog-title">
            <Typography variant="h6">{f({ id: "settings.title" })}</Typography>
            <CloseButton onClick={handleProfileDialogClose}>
              <CloseIcon />
            </CloseButton>
          </ProfileDialogTitle>
          <DialogContent dividers>
            <ProfileList>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText
                  primary={user.name}
                  secondary={f({ id: "settings.name" })}
                  data-private
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <MailIcon />
                </ListItemIcon>
                <ListItemText
                  primary={user.mail}
                  secondary={f({ id: "settings.mail" })}
                  data-private
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LockIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    editingPassword ? (
                      <Input
                        id="input-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        type="password"
                      />
                    ) : (
                      f({ id: "settings.password" })
                    )
                  }
                />
                <ListItemSecondaryAction>
                  {editingPassword ? (
                    <IconButton onClick={handlePasswordSubmit} size="large">
                      <CheckIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      edge="end"
                      aria-label="edit-password"
                      onClick={() => setEditingPassword(true)}
                      size="large"
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
              {user.oauth2clients &&
                user.oauth2clients.includes(ExternalProvider.AGRIWEBB) && (
                  <ListItem>
                    <ListItemIcon>
                      <AgriwebbLogoIcon />
                    </ListItemIcon>
                    <ListItemText primary={f({ id: "settings.agriwebb" })} />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label={f({ id: "settings.agriwebb.remove" })}
                        onClick={() =>
                          removeIntegration(ExternalProvider.AGRIWEBB)
                        }
                        size="large"
                        title={f({ id: "settings.agriwebb.remove" })}
                      >
                        <ClearIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
            </ProfileList>
            <Divider />
            <List>
              <ListItem button onClick={handleLogout}>
                <ListItemIcon>
                  <ExitToAppIcon />
                </ListItemIcon>
                <ListItemText primary={f({ id: "settings.logout" })} />
              </ListItem>
            </List>
          </DialogContent>
        </Dialog>
      )}
    </DrawerContainer>
  );
};

// Styled Components for the new Figma design
const DrawerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0.6) 100%
    ),
    linear-gradient(
      90deg,
      rgba(244, 244, 245, 1) 0%,
      rgba(244, 244, 245, 1) 100%
    );
  padding: 16px;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

const ContentWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 50px;
  min-height: 0;
`;

const NavButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

interface NavButtonProps {
  active: boolean;
}

const NavButton = styled.button<NavButtonProps>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${(props) => (props.active ? "#f4f4f5" : "transparent")};
  border: none;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${(props) => (props.active ? "#000000" : "#616161")};
  text-align: left;
  transition: background-color 0.2s;
  width: 100%;

  &:hover {
    background-color: ${(props) => (props.active ? "#f4f4f5" : "#fafafa")};
  }

  span {
    flex: 1;
    min-width: 0;
  }
`;

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-top: 16px;
  border-top: 1px solid #e4e4e7;
`;

const BottomNavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #71717a;
  text-align: left;
  transition: background-color 0.2s;
  width: 100%;

  &:hover {
    background-color: #fafafa;
  }

  span {
    flex: 1;
    min-width: 0;
  }
`;

const ProfileButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: transparent;
  border: none;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #71717a;
  text-align: left;
  width: 100%;
  min-width: 0;

  span {
    min-width: 0;
    flex: 1;
  }
`;

const ProfileDialogTitle = styled(DialogTitle)(
  ({ theme }) => `
  margin: 0;
  padding: ${theme.spacing(2)};
  position: relative;
  `
);

const CloseButton = styled(IconButton)(
  ({ theme }) => `
  position: absolute;
  right: ${theme.spacing(1)};
  top: ${theme.spacing(1)};
  `
);

const ProfileList = styled(List)(
  ({ theme }) => `
  padding-top: 0;
  min-width: 300px;
`
);

export default InitialSelectScreen;
